import { CalendarIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useEffect, useState } from "react";
import { useTranslation } from "@/i18n";

interface EnhancedDatePickerProps {
  date?: Date;
  onDateChange?: (date: Date | undefined) => void;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  minDate?: Date;
  maxDate?: Date;
}

const EnhancedDatePicker = ({
  date,
  onDateChange,
  className,
  disabled = false,
  placeholder,
  minDate,
  maxDate,
}: EnhancedDatePickerProps) => {
  const { t } = useTranslation();
  const months = t("datePicker.months", { returnObjects: true });
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(date);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState<number>(
    selectedDate?.getMonth() || new Date().getMonth(),
  );
  const [currentYear, setCurrentYear] = useState<number>(
    selectedDate?.getFullYear() || new Date().getFullYear(),
  );

  // Generate years based on min and max dates
  const getAvailableYears = () => {
    const currentYearNum = new Date().getFullYear();
    const startYear = minDate ? minDate.getFullYear() : currentYearNum - 110;
    const endYear = maxDate ? maxDate.getFullYear() : currentYearNum;

    return Array.from(
      { length: endYear - startYear + 1 },
      (_, i) => startYear + i,
    );
  };

  // Replace the years array with this
  const years = getAvailableYears();

  // Handle date selection
  const handleSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    if (date) {
      setCurrentMonth(date.getMonth());
      setCurrentYear(date.getFullYear());
    }
    onDateChange?.(date);
    setCalendarOpen(false);
  };

  // Handle month change
  const handleMonthChange = (value: string) => {
    const monthIndex = Number.parseInt(value);

    // Check if the selected month is valid with the current year
    if (
      minDate &&
      currentYear === minDate.getFullYear() &&
      monthIndex < minDate.getMonth()
    ) {
      return;
    }

    if (
      maxDate &&
      currentYear === maxDate.getFullYear() &&
      monthIndex > maxDate.getMonth()
    ) {
      return;
    }

    setCurrentMonth(monthIndex);
  };

  // Handle year change
  const handleYearChange = (value: string) => {
    const year = Number.parseInt(value);
    setCurrentYear(year);
  };

  // Update the calendar view when month or year changes
  useEffect(() => {
    if (selectedDate) {
      const newDate = new Date(selectedDate);
      newDate.setMonth(currentMonth);
      newDate.setFullYear(currentYear);
      setSelectedDate(newDate);
      onDateChange?.(newDate);
    }
  }, [currentMonth, currentYear]);

  return (
    <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "bg-card w-full justify-start text-left font-normal",
            !selectedDate && "text-muted-foreground",
            className,
          )}
          disabled={disabled}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {selectedDate
            ? selectedDate.toLocaleDateString(undefined, {
                year: "numeric",
                month: "long",
                day: "numeric",
              })
            : (placeholder ?? t("datePicker.placeholder"))}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex items-center justify-between border-b p-3">
          <div className="flex items-center gap-2">
            <Select
              value={currentMonth.toString()}
              onValueChange={handleMonthChange}
            >
              <SelectTrigger className="h-8 w-[130px]">
                <SelectValue placeholder={t("datePicker.month")} />
              </SelectTrigger>
              <SelectContent>
                {months.map((month, index) => {
                  // Disable months outside of min/max range
                  const isDisabled =
                    (minDate &&
                      currentYear === minDate.getFullYear() &&
                      index < minDate.getMonth()) ||
                    (maxDate &&
                      currentYear === maxDate.getFullYear() &&
                      index > maxDate.getMonth());

                  return (
                    <SelectItem
                      key={month}
                      value={index.toString()}
                      disabled={isDisabled}
                    >
                      {month}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>

            <Select
              value={currentYear.toString()}
              onValueChange={handleYearChange}
            >
              <SelectTrigger className="h-8 w-[90px]">
                <SelectValue placeholder={t("datePicker.year")} />
              </SelectTrigger>
              <SelectContent>
                {years.map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={handleSelect}
          month={new Date(currentYear, currentMonth)}
          onMonthChange={(date) => {
            setCurrentMonth(date.getMonth());
            setCurrentYear(date.getFullYear());
          }}
          disabled={(date) => {
            // Disable dates outside of min/max range
            return (
              (minDate ? date < minDate : false) ||
              (maxDate ? date > maxDate : false)
            );
          }}
          hidden={[{ before: minDate! }, { after: maxDate! }]}
          autoFocus
        />
      </PopoverContent>
    </Popover>
  );
};

export default EnhancedDatePicker;
